import { Button, Col, Dropdown, Empty, Grid, Spin, Tag } from "antd";
import { getChatMembers, getStatusColor } from "./helper";
import {
  InfoCircleOutlined,
  FileTextOutlined,
  LeftOutlined,
  MoreOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInfiniteMessages } from "../../../../hooks/useMessages";
import Avatar from "../../../../components/generic/Avatar";
import ChatInput from "./ChatInput";
import { selectUser } from "../../../../store/user/selectors";
import MessageItem from "./MessageItem";
import CreateMeetingModal from "./CreateMeetingModal";
import { setKeyValue } from "../../../../store/chats/slice";
import { getFullName } from "../../../../utils/shared";
import LogoImage from "../../../../assets/images/logo.png";
import ReactionsModal from "../../../../components/shared/Reactions/ReactionsModal";
import { selectSelectedReactions } from "../../../../store/chats/selectors";
const { useBreakpoint } = Grid;

const ChatWindow = (props) => {
  const {
    isShowTicketInfo,
    setIsShowTicketInfo,
    isShowProductivity,
    setIsShowProductivity,
  } = props?.options || {};
  const { md } = useBreakpoint();

  const user = useSelector(selectUser);

  const reactions = useSelector(selectSelectedReactions);

  const dispatch = useDispatch();
  const {
    isLoading,
    scrollContainerRef,
    observerTarget,
    selectedChat,
    selectedTenantId,
    onSendMessage,
  } = useInfiniteMessages();

  const users = useMemo(() => {
    return getChatMembers(selectedChat);
  }, [selectedChat]);

  const handleNotesClick = useCallback(() => {
    setIsShowProductivity(!isShowProductivity);
    if (isShowTicketInfo) setIsShowTicketInfo(false);
  }, [isShowTicketInfo, isShowProductivity]);

  const handleTicketInfoClick = useCallback(() => {
    setIsShowTicketInfo(!isShowTicketInfo);
    if (isShowProductivity) setIsShowProductivity(false);
  }, [isShowTicketInfo, isShowProductivity]);

  useEffect(() => {
    if (
      (!isShowTicketInfo || !isShowProductivity) &&
      scrollContainerRef?.current
    ) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef?.current?.scrollHeight;
    }
  }, [isShowTicketInfo, isShowProductivity]);

  const { avatarUser, chatTitle } = useMemo(() => {
    let avatarUser = null;

    if (selectedChat?.customer) {
      avatarUser = selectedChat.customer;
    } else if (selectedChat?.members?.length === 2) {
      avatarUser = selectedChat.members.find(({ id }) => user?.user_id !== id);
    }

    const chatTitle = selectedChat?.name
      ? selectedChat?.name
      : avatarUser
        ? getFullName(avatarUser)
        : selectedChat?.members
            ?.filter(({ id }) => id !== user?.user_id)
            ?.map(({ first_name, last_name }) =>
              `${first_name || ""} ${last_name?.[0] || ""}`.trim(),
            )
            ?.join(", ") || "";

    return { avatarUser, chatTitle };
  }, [selectedChat, user?.user_id]);

  return (
    <>
      {(md || (!isShowTicketInfo && !isShowProductivity)) && (
        <Col
          xs={24}
          md={isShowTicketInfo ? 17 : isShowProductivity ? 14 : 24}
          className="chat-window"
        >
          {selectedChat ? (
            <>
              <div className="chat-header flex justify-space-between align-center">
                <div className="header-left flex align-center">
                  {!md && (
                    <Button
                      type="text"
                      shape="circle"
                      icon={<LeftOutlined />}
                      className="back-button"
                      onClick={() => {
                        dispatch(
                          setKeyValue({
                            key: "selectedChatId",
                            value: null,
                          }),
                        );
                      }}
                    />
                  )}
                  <div className="avatar-section">
                    {avatarUser ? (
                      <Avatar size={40} user={avatarUser} />
                    ) : (
                      <TeamOutlined className="group-icon" />
                    )}
                  </div>
                  <div className="customer-info">
                    <div className="name-row">
                      <div className="name ellipsis-text">{chatTitle}</div>
                    </div>
                    {md && (
                      <div className={"header-bottom"}>
                        <span className="ticket-badge ellipsis-text">
                          #{selectedChat.display_id}
                          {selectedChat.branch
                            ? ` 📍 ${selectedChat.branch.name}`
                            : ""}
                        </span>
                        {selectedChat.topic && (
                          <span className="topic-label">
                            {selectedChat.topic.name}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="header-actions">
                  {!md ? (
                    <>
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "meeting",
                              label: (
                                <CreateMeetingModal
                                  selectedChat={selectedChat}
                                  user={user}
                                  md
                                />
                              ),
                            },
                            {
                              key: "notes",
                              label: (
                                <span onClick={handleNotesClick}>
                                  <FileTextOutlined
                                    style={{ marginRight: 8 }}
                                  />
                                  Internal Notes
                                </span>
                              ),
                            },
                            {
                              key: "info",
                              label: (
                                <span onClick={handleTicketInfoClick}>
                                  <InfoCircleOutlined
                                    style={{ marginRight: 8 }}
                                  />
                                  Ticket Info
                                </span>
                              ),
                            },
                          ],
                        }}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <Button
                          type="text"
                          icon={<MoreOutlined />}
                          className="action-btn"
                        />
                      </Dropdown>
                    </>
                  ) : (
                    <>
                      <Tag
                        color={getStatusColor(selectedChat?.status)}
                        className="status-tag"
                      >
                        {selectedChat?.status?.toUpperCase()}
                      </Tag>
                      <CreateMeetingModal
                        selectedChat={selectedChat}
                        user={user}
                      />
                      <Button
                        type="text"
                        icon={<FileTextOutlined />}
                        className="action-btn"
                        title="Internal Notes"
                        onClick={handleNotesClick}
                      />
                      <Button
                        type="text"
                        icon={<InfoCircleOutlined />}
                        className="action-btn"
                        title="Ticket Info"
                        onClick={handleTicketInfoClick}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="messages-area" ref={scrollContainerRef}>
                <div ref={observerTarget} />

                {isLoading && <Spin />}
                {selectedChat?.messages?.map((msg) => (
                  <MessageItem
                    key={msg.id}
                    message={msg}
                    isCurrentUser={user?.user_id === msg.sender_id}
                    users={users}
                    loggedUser={user}
                    selectedTenantId={selectedTenantId}
                  />
                ))}
              </div>

              <ChatInput
                onSendMessage={onSendMessage}
                selectedChat={selectedChat}
                selectedTenantId={selectedTenantId}
              />
            </>
          ) : (
            <div className="flex justify-center h-100 w-100 align-center">
              <Empty description="Select a conversation" image={LogoImage} />
            </div>
          )}
        </Col>
      )}
      {reactions && (
        <ReactionsModal
          onCancel={() =>
            dispatch(
              setKeyValue({
                key: "reactions",
                value: null,
              }),
            )
          }
          users={users}
          reactions={reactions}
        />
      )}
    </>
  );
};
export default ChatWindow;
